// src/components/UserManagement.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchUsers } from '../../api/users';
import UserTable from './UserTable';
import { useAuthState } from "../../Context";
import { Typography, Box, FormControlLabel, Checkbox, FormGroup } from '@mui/material';

const UserManagement = () => {
  const { session } = useAuthState();
  const token = session.getIdToken().getJwtToken();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20); // Default to 10 users per page
  const [nextTokenHistory, setNextTokenHistory] = useState([null]);
  const [includeServiceAccounts, setIncludeServiceAccounts] = useState(false); // Default unchecked
  const [includeDeloitteUsers, setIncludeDeloitteUsers] = useState(true); // Default checked

  const getExcludeDomains = () => {
    const excludeDomains = [];
    if (!includeServiceAccounts) {
      excludeDomains.push('gaaccess.com');
    }
    if (!includeDeloitteUsers) {
      excludeDomains.push('deloitte.com');
    }
    return excludeDomains.length > 0 ? excludeDomains.join(',') : null;
  };

  const { data, error, isLoading, isError, refetch } = useQuery({
    queryKey: ['users', token, rowsPerPage, nextTokenHistory[page], includeServiceAccounts, includeDeloitteUsers],
    queryFn: () => fetchUsers({
      token,
      limit: rowsPerPage,
      nextToken: nextTokenHistory[page],
      excludeDomains: getExcludeDomains()
    }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data) {
      if (page === nextTokenHistory.length - 1) {
        setNextTokenHistory([...nextTokenHistory, data.nextToken || null]);
      }

      // Prefetch the next page's data
      if (data.nextToken) {
        queryClient.prefetchQuery({
          queryKey: ['users', token, rowsPerPage, data.nextToken, includeServiceAccounts, includeDeloitteUsers],
          queryFn: () => fetchUsers({
            token,
            limit: rowsPerPage,
            nextToken: data.nextToken,
            excludeDomains: getExcludeDomains()
          })
        });
      }
    }
  }, [data, page, nextTokenHistory, token, rowsPerPage, queryClient, includeServiceAccounts, includeDeloitteUsers]);

  useEffect(() => {
    refetch();
  }, [page, rowsPerPage, refetch, includeServiceAccounts, includeDeloitteUsers]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 20));
    setPage(0);
    setNextTokenHistory([null]); // Reset nextTokenHistory when rows per page changes
  };

  const handleIncludeServiceAccountsChange = (event) => {
    setIncludeServiceAccounts(event.target.checked);
    setPage(0);
    setNextTokenHistory([null]); // Reset nextTokenHistory when includeServiceAccounts changes
  };

  const handleIncludeDeloitteUsersChange = (event) => {
    setIncludeDeloitteUsers(event.target.checked);
    setPage(0);
    setNextTokenHistory([null]); // Reset nextTokenHistory when includeDeloitteUsers changes
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Users
      </Typography>
      <Box display="flex" flexDirection="column" alignItems="flex-start" mb={2}>
        <FormGroup row>
          <FormControlLabel
            control={<Checkbox size="small" checked={includeServiceAccounts} onChange={handleIncludeServiceAccountsChange} />}
            label={<Typography variant="body2">Include Service Accounts</Typography>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={includeDeloitteUsers} onChange={handleIncludeDeloitteUsersChange} />}
            label={<Typography variant="body2">Include Deloitte Users</Typography>}
          />
        </FormGroup>
      </Box>
      <UserTable
        data={data}
        isLoading={isLoading}
        isError={isError}
        error={error}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default UserManagement;
